<template>
  <GcBaseLayout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :backButtonEnable="false"
    :formsConfig="formsConfig"
    :tabs="getTabs"
    :selectedIndex="getSelectedIndex"
    ref="gc-base-layout"
    class="point-of-sale"
  >
    <slot> </slot>
    <template #headerContent>
      <GcHeaderContentDynamic
        :title="'Point of Sale'"
        :createModeIsEditMode="true"
        :baseView="baseView"
        :primaryLabel="'Point of Sale'"
        :secondaryLabel="undefined"
        v-if="loaded"
      />
    </template>

    <gc-barcode-parser @barcodeScanned="barcodeScanned" />

    <div class="pos-entry">
      <div class="css-v2 transaction-screen">
        <div class="header-form" v-if="loaded">
          <gc-form
            :formOptions="{
              gcLabelLocation: 'left',
            }"
            :formItems="getHeaderFormItems"
            :isNormal="true"
            :data-model="loaded ? model : {}"
            :formMode="baseView.mode"
            :fromFrom="'gc-header-form'"
            ref="gc-header-form"
            :valueChangeCallBack="headerFormValueChanged"
            :checkValid="isValid"
            :validationGroup="'gc-header-form'"
            :scrollingEnabled="false"
            :formHeight="30"
            :formContentReady="onHeaderFormContentReady"
          ></gc-form>
        </div>

        <div class="details-grid gc-details-grid" v-if="loaded">
          <dx-data-grid
            :columns="getColumns"
            :data-source="getLocalDataSource"
            :columnChooser="{ enabled: false }"
            :repaint-changes-only="true"
            :show-borders="true"
            :two-way-binding-enabled="false"
            :focusedRowEnabled="false"
            :key-expr="'ID'"
            :ref="'dx-data-grid-comp'"
            :height="getGridHeight"
            @editor-preparing="onEditorPreparing"
            @cell-prepared="onCellPrepared"
            @row-removed="onRowRemoved"
          >
            <DxPaging :page-size="50" :enabled="true" />

            <DxPager :visible="false" />

            <DxScrolling
              :mode="'virtual'"
              :row-rendering-mode="'virtual'"
              :preloadEnabled="true"
            />

            <DxEditing
              :allow-updating="true"
              :allow-deleting="true"
              mode="cell"
              :use-icons="true"
              :refreshMode="'reshape'"
              :changes="changes"
              :texts="{
                confirmDeleteTitle: 'Delete Entry',
                confirmDeleteMessage:
                  'Are you sure to delete the entry ? This action cannot be undone.',
              }"
            />

            <template #sales-um-edit-cell-template="{ data: cellInfo }">
              <div
                v-bind:class="
                  cellInfo.data.SalesUMs.length < 2
                    ? 'cell-disabled'
                    : 'gc-editable-cell-color'
                "
              >
                <DxTextBox
                  :value="cellInfo.value"
                  class="edit-cell-textbox-field"
                  :readOnly="cellInfo.data.SalesUMs.length < 2"
                  @focus-in="onSalesUMFocusIn($event, cellInfo)"
                />
              </div>
            </template>
          </dx-data-grid>

          <div class="footer">
            <div class="permission">
              <div>
                <dx-check-box
                  :text="'Apply Discount'"
                  v-model="IsDiscountUpdate"
                  :value="IsDiscountUpdate"
                  :on-value-changed="setDiscountUpdate"
                ></dx-check-box>
              </div>
              <div>
                <dx-check-box
                  :text="'Price Edit'"
                  v-model="IsPriceUpdate"
                  :value="IsPriceUpdate"
                  :on-value-changed="setPriceUpdate"
                ></dx-check-box>
              </div>
            </div>
            <div class="totals">
              <div>Item Count : {{ getTotalItemCount }}</div>
              <div>Total : {{ getTotalAmount }}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="num-pad" v-if="loaded">
        <div>
          <div>
            <gc-number-pad
              :focusedCellData="focusedCellData"
              :applyFocusClass="applyFocusClass"
            />
          </div>
          <div class="totals">
            <div class="sub topline">
              <span>Subtotal</span>
              <span>{{ getSubTotal }}</span>
            </div>
            <div class="sub topline">
              <span>Tax</span>
              <span>{{ getTaxTotal }}</span>
            </div>
            <div class="sub topline">
              <span>Rounding</span>
              <span>{{ getRoundingAmount }}</span>
            </div>
            <hr />
            <div class="sub last">
              <span>Total</span>
              <span>{{ getTotalDue }}</span>
            </div>
            <div class="sub last">
              <span>Payment</span>
              <span>{{ getTotalPayment }}</span>
            </div>
            <div class="sub last">
              <span>Balance</span>
              <span>{{ getTotalBalance }}</span>
            </div>
          </div>
        </div>

        <div class="action-btns">
          <DxButton
            text="Payment"
            type="default"
            styling-mode="contained"
            :ref="'payment-btn'"
            :disabled="getTotalItemCount == 0"
            @click="visiblePayment = !visiblePayment"
          />
          <DxButton
            text="Process Sale"
            type="normal"
            styling-mode="contained"
            :disabled="!isProcessSaleEnabled"
            :ref="'sales-btn'"
            @click="onProcessSale"
          />
          <DxButton
            text="Print Receipt"
            type="normal"
            styling-mode="contained"
            :disabled="!IsPrintReceipt"
            :ref="'receipt-btn'"
            @click="visibleReceipt = !visibleReceipt"
          />
        </div>
      </div>
    </div>

    <!-- Customer Select Popup Model -->
    <DxPopup
      :visible="visibleCustomerSearch"
      :drag-enabled="true"
      :hide-on-outside-click="false"
      :show-title="true"
      :width="'75%'"
      :height="680"
      :title="'Customer Search'"
      @hidden="popupHidden"
      :showCloseButton="false"
      :wrapper-attr="{ class: 'customer-search-popup css-v2 gc-popup' }"
      v-if="visibleCustomerSearch"
    >
      <div v-if="visibleCustomerSearch">
        <dx-data-grid
          :height="580"
          :data-source="getCustomerDataSource"
          :columns="getCustomerColumns"
          :ref="'customer-selection-grid'"
          :column-chooser="{
            enabled: false,
          }"
          :repaintChangesOnly="false"
          :twoWayBindingEnabled="true"
          :pager="{ visible: false }"
          @selection-changed="onCustomerSelectionChanged"
          @row-prepared="onRowPrepared"
        >
          <DxScrolling
            :mode="'virtual'"
            :row-rendering-mode="'virtual'"
            :preload-enabled="true"
          />

          <DxPaging :page-size="50" :enabled="false" />

          <template #status-template="{ data }">
            <div>
              <gc-status-chip
                :status="'Credit Hold'"
                v-if="data.data.StopCredit == 'Y'"
              />
            </div>
          </template>
        </dx-data-grid>
      </div>
      <div class="footer">
        <DxButton
          text="Cancel"
          type="default"
          styling-mode="contained"
          @click="popupHidden"
        />
      </div>
    </DxPopup>

    <!-- Search Products PopUp -->
    <DxPopup
      :visible="visibleProductSearch"
      :drag-enabled="true"
      :hide-on-outside-click="false"
      :show-title="true"
      :width="'75%'"
      :height="680"
      :title="'Product Search'"
      @hidden="popupHidden"
      :showCloseButton="false"
      :wrapper-attr="{ class: 'search-products-popup css-v2 gc-popup' }"
    >
      <div class="search-products">
        <div class="css-v2 transaction-list">
          <dx-data-grid
            v-if="visibleProductSearch"
            :data-source="getProductDataSource"
            :columns="getProductColumns"
            :height="580"
            :columnChooser="{
              enabled: false,
            }"
            @selection-changed="onProductSelectionChanged"
          >
            <DxSelection
              :select-all-mode="'allPages'"
              :show-check-boxes-mode="'always'"
              mode="multiple"
              :allowSelectAll="false"
            />
            <DxPaging :page-size="50" :enabled="true" />

            <DxPager :visible="false" />

            <DxScrolling
              :mode="'virtual'"
              :row-rendering-mode="'virtual'"
              :preloadEnabled="true"
            />
          </dx-data-grid>
        </div>
      </div>

      <div class="footer edit-product-item-popup-footer">
        <DxButton
          text="Cancel"
          type="danger"
          styling-mode="contained"
          @click="popupHidden"
        />

        <DxButton
          text="Select"
          type="default"
          styling-mode="contained"
          @click="onSelectedProduct"
          :disabled="true"
          :ref="'submit-btn'"
        />
      </div>
    </DxPopup>

    <GcPopUpSelectRecord
      v-if="popupVisible"
      :popupVisible="popupVisible"
      :popupHiddenCallBack="popupHidden"
      :popupConfig="popupConfig"
      :dataModel="selectPopupModel"
    />

    <!-- Payment Select Popup Model -->
    <DxPopup
      :visible="visiblePayment"
      :drag-enabled="true"
      :hide-on-outside-click="false"
      :show-title="true"
      :width="'75%'"
      :height="680"
      :title="'Choose payment method'"
      @hidden="popupHidden"
      :showCloseButton="false"
      :wrapper-attr="{ class: 'payment-popup css-v2 gc-popup' }"
      v-if="visiblePayment"
    >
      <div v-if="visiblePayment">
        <div class="total-counts">
          <div class="item-count">
            <span>Item Count</span>
            <span>{{ getTotalItemCount }}</span>
          </div>
          <div class="totals">
            <div>
              <span>Total</span>
              <span>{{ getTotalAmount }}</span>
            </div>
            <div>
              <span>Paid</span>
              <span>{{ getTotalPayment }}</span>
            </div>
          </div>
        </div>

        <div class="btns-container">
          <div>
            <Dx-Button
              text="Cheque"
              :height="100"
              :width="400"
              @click="onShowChequeOption"
            />
            <Dx-Button
              text="Cash"
              :height="100"
              :width="400"
              @click="setCashPayment"
            />
          </div>
          <div>
            <Dx-Button
              text="Card"
              :height="100"
              :width="400"
              @click="onShowCardOption"
            />
            <Dx-Button
              text="Account"
              :height="100"
              :width="400"
              :disabled="isCustomerAccountEnabled"
              @click="onAccountPayment"
            />
          </div>
        </div>
      </div>
      <div class="footer">
        <DxButton
          text="Cancel"
          type="default"
          styling-mode="contained"
          @click="popupHidden"
        />
      </div>
    </DxPopup>

    <!-- Cash Payment Popup Model -->
    <DxPopup
      :visible="visibleCashPayment"
      :drag-enabled="true"
      :hide-on-outside-click="false"
      :show-title="true"
      :width="650"
      :height="580"
      :title="'Cash Payment'"
      @hidden="popupHidden"
      @shown="onCashPaymentShown"
      :showCloseButton="false"
      :wrapper-attr="{ class: 'cash-popup css-v2 gc-popup' }"
      v-if="visibleCashPayment"
    >
      <div v-if="visibleCashPayment" class="cash-content">
        <div>
          <dx-number-box
            width="236"
            format="##,###,###,###,##0.00"
            :read-only="true"
            ref="cash-num-box"
            v-model="totalCashReceived"
          />
          <gc-number-pad
            :focusedCellData="focusedCellData"
            :applyFocusClass="applyFocusClass"
          />
        </div>
        <div>
          <div>
            <div class="top-line">
              <span class="label">Total</span>
              <span class="value">{{ getTotalAmount }}</span>
            </div>
            <div class="top-line">
              <span class="label">Rounding</span>
              <span class="value">{{ getRoundingAmount }}</span>
            </div>
            <div class="top-line">
              <span class="label">Total Due</span>
              <span class="value">{{ getTotalDue }}</span>
            </div>
            <hr />
            <div>
              <span class="label">Cash Received</span>
              <span class="value">{{ getTotalCashReceived }}</span>
            </div>
            <div>
              <span class="label">Change Due</span>
              <span class="value">{{ getTotalChangedDue }}</span>
            </div>

            <hr />
          </div>

          <div>
            <DxButton
              text="CONFIRM PAYMENT"
              type="normal"
              styling-mode="contained"
              :disabled="totalCashReceived < totalFXLineTotal + totalRounding"
              :ref="'receipt-btn'"
              @click="onConfirmPayment"
            />
          </div>
        </div>
      </div>
      <div class="footer">
        <DxButton
          text="Cancel"
          type="default"
          styling-mode="contained"
          @click="cancelCashModel"
        />
      </div>
    </DxPopup>

    <!-- View Receipt Popup Model -->
    <DxPopup
      :visible="visibleReceipt"
      :drag-enabled="true"
      :hide-on-outside-click="false"
      :show-title="true"
      :width="'60%'"
      :height="'75%'"
      :title="'Print Receipt'"
      @hidden="popupHidden"
      @shown="onPrintReceiptShown"
      :showCloseButton="false"
      :wrapper-attr="{ class: 'receipt-popup css-v2 gc-popup' }"
      v-if="visibleReceipt"
    >
      <div v-if="visibleReceipt" class="receipt-content">
        <!-- <iframe :src="receiptURL" title="description"></iframe> -->
        <embed
          :src="receiptURL"
          :width="'100%'"
          :height="'100%'"
          type="application/pdf"
          v-if="receiptURL"
        />
      </div>
      <div class="footer">
        <DxButton
          text="Cancel"
          type="default"
          styling-mode="contained"
          @click="visibleReceipt = false"
        />
      </div>
    </DxPopup>

    <!-- Price / Discount Permission Popup Model -->
    <DxPopup
      :visible="visiblePermission"
      :drag-enabled="true"
      :hide-on-outside-click="false"
      :show-title="true"
      :width="'400px'"
      :height="'270px'"
      :title="PermissionTitle"
      @hidden="popupHidden"
      :showCloseButton="false"
      :wrapper-attr="{ class: 'permission-popup css-v2 gc-popup' }"
      v-if="visiblePermission"
    >
      <div v-if="visiblePermission" class="permission-content">
        <DxForm
          :form-data.sync="permissionFormData"
          :show-colon-after-label="false"
          :show-required-mark="false"
          :show-validation-summary="false"
          :scrolling-enabled="false"
          :height="140"
          :validation-group="'permission-form'"
          :col-count-by-screen="{
            xs: 1,
            sm: 1,
            md: 1,
            lg: 1,
          }"
          :label-location="'top'"
          :items.sync="getPermissionFormItems"
          ref="gc-form-comp"
        >
        </DxForm>
      </div>
      <div class="footer">
        <DxButton
          text="Cancel"
          type="normal"
          styling-mode="contained"
          @click="onCancelPermission"
        />
        <DxButton
          text="Access"
          type="default"
          styling-mode="contained"
          @click="getUserPermission"
        />
      </div>
    </DxPopup>
  </GcBaseLayout>
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import {
  PointOfSalesTab,
  GetIndexByTabName,
} from "../../../_models/tabdata/sales/point-of-sales-tabs";

import { PointOfSalesService } from "../../../_services/api/sales/pos/point-of-sale.service";
import { CustomerService } from "../../../_services/api/customers/customer.service";
import { ProductService } from "../../../_services/api/products/product.service";
import { DefaultCompanyBranchDivisionService } from "../../../_services/api/sales/default-company-branch-dicision.service";
import ColumnWidth from "../../../_helpers/data-grid/ColumnWidth";
import ArrayStore from "devextreme/data/array_store";
import { mapGetters } from "vuex";

import { formatNumber } from "devextreme/localization";
import { SalesOrderService } from "../../../_services/api/sales/sales-order.service";
import { getFactorByCode, getGlobalField } from "../../../_store/helpers";
import { CompanyCurrencyService } from "../../../_services/api/accounts/company-currency.service";
import { SalesOrderV3Service } from "../../../_services/api/sales/sales-order-v3.service";
import DataSource from "devextreme/data/data_source";
import salesFormulas from "../../../_helpers/formulas/sales-formulas";
// import { SalesOrderState } from "../../../_models/sales-order";
// import { SalesOrderTypeService } from "../../../_services/api/sales/sales-order-type.service";
import { custom } from "devextreme/ui/dialog";
import { CustomerDeliveryAddressService } from "../../../_services/api/customers/customer-delivery-address.service";
import ApiService from "../../../_services/api/api.service";

const {
  // calculateGrossProfitPcnt,
  // calculateSalesUMPriceForGP,
  // calculateNetLineTotal,
  // calculateWetLineTotal,
  // calculateTaxableLineTotal,
  // calculateTaxLineTotal,
  // calculateLineTotal,
  calculateSalesUMNetFXPrice,
  // calculateStockUMPrice,
  // calculateStockUMNetPrice,
  // calculateStockUMFXPrice,
  // calculateStockUMQty,
  // calculateStockUMNetFXPrice,
  // calculateDiscLineTotal,
  // calculateDiscFXLineTotal,
  // calculateNetFXLineTotal,
  // calculateWetFXLineTotal,
  // calculateTaxableFXLineTotal,
  // calculateTaxFXLineTotal,
  // calculateFXLineTotal,
  // calculateSalesUMNetPriceFromSalesUMNetFXPrice,
  // calculateSalesUMPriceFromSalesUMFXPrice,
  // calculateCalculatedDiscPcnt,
  // calculateSalesUMFXPriceFromSalesUMNetFXPrice,
  // calculateSalesUMNetFXPriceFromSalesUMFXPrice,
} = salesFormulas;

import { b64toBlob } from "../../../_helpers/pdf/pdf-helper";
import validationEngine from "devextreme/ui/validation_engine";
import { TransactionReportService } from "../../../_services/api/reports/transaction-report.service";

export default {
  mixins: [RouteMixin, FormMixin],
  data() {
    return {
      loaded: false,
      isValid: false,
      model: {
        DateIn: new Date(),
        RequiredByDate: new Date(),
        OrderNo: undefined,
        CustomerCode: undefined,
        CustomerName: undefined,
        CompanyCode: undefined,
        BranchCode: undefined,
        DivisionCode: undefined,
        CurrencyCode: undefined,
        CompanyCurrency: undefined,
        DefaultRepCode: undefined,
        TermCalculation: undefined,
        CreditLimit: 0,
      },
      visibleCustomerSearch: false,
      visibleProductSearch: false,
      selectedProducts: [],
      localDataSource: new ArrayStore({
        key: "ID",
        data: [],
      }),
      headerFormHeight: 0,
      focusedCellData: undefined,
      changes: [],
      IsPriceUpdate: false,
      IsDiscountUpdate: false,
      PriceUpdateTimeOutID: undefined,
      priceChangeTimeId: undefined,
      popupVisible: false,
      popupConfig: undefined,
      selectPopupModel: {
        filterBySalesRep: undefined,
      },
      selectedRowData: undefined,
      visiblePayment: false,
      visibleCashPayment: false,
      totalCashReceived: 0,
      totalFXLineTotal: 0,
      totalRounding: 0,
      totalPayment: 0,
      totalBalance: 0,
      totalChangedDue: 0,
      PaymentMethod: undefined,
      TaxableFXLineTotal: 0,
      TaxFXLineTotal: 0,
      IsPrintReceipt: false,
      visibleReceipt: false,
      ReceiptCode: undefined,
      receiptURL: undefined,
      DiscountEditPermissionReceived: false,
      PermissionTitle: undefined,
      visiblePermission: false,
      permissionFormData: {
        userName: undefined,
        password: undefined,
      },
      permissionLoading: false,
      PermissionDescription: undefined,
      PriceEditPermissionReceived: false,
      intervalId: null,
    };
  },
  mounted() {
    this.startConsuming();
  },
  beforeDestroy() {
    this.stopConsuming();
  },
  async created() {
    this.showLoading(true);
    await this.onInitialized();
    this.loaded = true;

    this.showLoading(false);
  },
  computed: {
    ...mapGetters({ gridHeight: "ui/getHeightForDataGrid" }),
    ...mapGetters({
      numericValue: "numericInputStore/getNumericValue",
      sourceNumericValue: "numericInputStore/getSourceNumericValue",
      sourceNumericRecord: "numericInputStore/getSourceNumericRecord",
      fieldFocused: "numericInputStore/getFieldFocused",
      keyPadLastEntry: "numericInputStore/getLastEntry",
    }),
    ...mapGetters({
      currentBarcodeFromQueue: "posScans/getCurrentItem",
    }),
    getTabs() {
      return PointOfSalesTab;
    },
    getSelectedIndex() {
      return GetIndexByTabName("New Transaction");
    },
    getHeaderFormItems() {
      return [
        {
          itemType: "group",
          caption: "",
          cssClass: "header-form-items",
          colCount: 4,
          // colSpan: 4,
          items: [
            {
              itemType: "group",
              caption: "",
              colCount: 2,
              colSpan: 3,
              items: [
                {
                  label: {
                    text: "Customer",
                  },
                  dataField: "CustomerName",
                  editorOptions: {
                    placeholder: "Customer",
                    tabIndex: -1,
                    onFocusIn: (e) => {
                      this.onFocusInCustomer(e);
                    },
                  },
                },
                {
                  screenField: "OrderNo",
                  dataField: "OrderNo",
                  editorOptions: {
                    placeholder: "Order No",
                    maxLength: 20,
                    onValueChanged: this.onOrderNoChanged,
                    tabIndex: -1,
                  },
                },
              ],
            },
            {
              itemType: "group",
              caption: "",
              colCount: 1,
              colSpan: 1,
              cssClass: "prod-search-btn",
              items: [
                {
                  dataField: "Generate",
                  label: {
                    text: "",
                    visible: false,
                  },
                  editorType: "dxButton",
                  editorOptions: {
                    validationGroup: "gc-header-form",
                    onClick: async (e) => {
                      if (e) this.setVisibleProductSearch();
                    },
                    text: "Search Product",
                    type: "default",
                    rtlEnabled: false,
                  },
                },
              ],
            },
          ],
        },
      ];
    },
    getColumns() {
      return [
        {
          caption: "Product",
          dataField: "ItemCode",
          allowEditing: false,
        },
        {
          caption: "Description",
          dataField: "ProductDescription",
          allowEditing: false,
        },
        {
          caption: "UM",
          dataField: "UnitOfMeasure",
          width: ColumnWidth.UnitOfMeasure.width,
          minWidth: ColumnWidth.UnitOfMeasure.minWidth,
          allowEditing: true,
          editCellTemplate: "sales-um-edit-cell-template",
          setCellValue: this.onSalesUMChanged,
        },
        {
          caption: "Quantity",
          dataField: "SalesUMQty",
          width: ColumnWidth.Quantity.width,
          minWidth: ColumnWidth.Quantity.minWidth,
          format: "##,###,###,###,##0.0000",
          cssClass: "gc-editable-cell-color",
          editorOptions: {
            format: "##,###,###,###,##0.0000",
            elementAttr: {
              class: "gc-grid-number-box-editor",
            },
            onFocusIn: (e, cellInfo) => {
              this.onFieldFocusIn(e, cellInfo);
            },
          },
          setCellValue: this.onSalesUMQtyChanged,
        },
        {
          caption: "Price",
          dataField: "SalesUMTaxIncFXPrice",
          width: ColumnWidth.Price4DecimalPoints.width,
          minWidth: ColumnWidth.Price4DecimalPoints.minWidth,
          format: "$ ##,###,###,###,##0.00",
          cssClass: this.IsPriceUpdate ? "gc-editable-cell-color" : "",
          editorOptions: {
            format: "$ ##,###,###,###,##0.00",
            elementAttr: {
              class: "gc-grid-number-box-editor",
            },
            onFocusIn: (e, cellInfo) => {
              this.onFieldFocusIn(e, cellInfo);
            },
          },
          allowEditing: this.IsPriceUpdate,
          setCellValue: this.onSalesUMFXPriceChanged,
        },
        {
          caption: "Discount",
          dataField: "DiscPcnt",
          cssClass: this.IsDiscountUpdate ? "gc-editable-cell-color" : "",
          width: ColumnWidth.Discount.width,
          minWidth: ColumnWidth.Discount.minWidth,
          format: "##0.00 '%'",
          editorOptions: {
            format: "##0.00 '%'",
            elementAttr: {
              class: "gc-grid-number-box-editor",
            },
            onFocusIn: (e, cellInfo) => {
              this.onFieldFocusIn(e, cellInfo);
            },
            min: 0,
            max: 100,
          },
          allowEditing: this.IsDiscountUpdate,
          setCellValue: this.onDiscPcntChanged,
        },
        {
          caption: "Inc Total",
          dataField: "FXLineTotal",
          width: ColumnWidth.Price4DecimalPoints.width,
          minWidth: ColumnWidth.Price4DecimalPoints.minWidth,
          format: "$ ##,###,###,###,##0.00",
          allowEditing: false,
        },
        {
          caption: "",
          dataField: "",
          type: "buttons",
          buttons: [
            {
              name: "copy",
              icon: "copy",
              onClick: (e) => {
                this.onCopyLine(e);
              },
            },
            {
              name: "delete",
            },
          ],
        },
      ];
    },
    getLocalDataSource() {
      // return this.localDataSource;
      return new DataSource({
        store: this.localDataSource._array,
      });
    },
    getGridHeight() {
      if (this.headerFormHeight)
        return this.gridHeight - this.headerFormHeight - 90;
      else return this.gridHeight - 100;
    },

    // Selection Popup Models
    getCustomerDataSource() {
      return CustomerService.dataSourceConfig("api/Customer", {
        select: [
          "Code",
          "Name",
          "StopCredit",
          "Inactive",
          "CurrencyCode",
          "CreditLimit",
          "TermCalculation",
        ],
        filter: [["Inactive", "=", "N"], "and", ["Status", "=", "A"]],
        sort: [{ selector: "Name", asc: true }],
      });
    },
    getCustomerColumns() {
      return [
        {
          caption: "Customer",
          dataField: "Name",
          cellTemplate: (data, info) => {
            return (data.innerHTML = `<div class="custom-auto-complete">
                  <span class="auto-complete-primary">
                    ${info.data.Name}
                  </span>
                <span class="record-secondary gc-grid-code">
                  ${info.data.Code}
                </span>
              </div>`);
          },
          sortIndex: 0,
          sortOrder: "asc",
        },
        {
          caption: "Code",
          dataField: "Code",
          visible: false,
        },
        {
          caption: "",
          dataField: "StopCredit",
          cellTemplate: "status-template",
          width: 150,
        },
      ];
    },
    getProductDataSource() {
      return ProductService.dataSourceConfig(
        "api/POSProduct?&$expand=SalesUMs(select=UnitOfMeasureCode)",
        {
          filter: [
            ["BranchCode", "=", `${this.model.BranchCode}`],
            "and",
            ["CompanyCode", "=", `${this.model.CompanyCode}`],
            "and",
            ["IsDefault", "=", "Y"],
          ],
          key: ["ItemCode", "BranchCode"],
        }
      );
    },
    getProductColumns() {
      return [
        {
          caption: "Product",
          dataField: "ProductDescription",
          cellTemplate: (data, info) => {
            return (data.innerHTML = `<div class="custom-auto-complete">
                  <span class="auto-complete-primary">
                    ${info.data.ProductDescription}
                  </span>
                <span class="record-secondary gc-grid-code">
                  ${info.data.ItemCode}
                </span>
              </div>`);
          },
          sortIndex: 0,
          sortOrder: "asc",
        },
        {
          caption: "ItemCode",
          dataField: "ItemCode",
          visible: false,
        },
      ];
    },
    getTotalItemCount() {
      let count = this.localDataSource._array.reduce(
        (sum, item) => sum + item.SalesUMQty,
        0
      );

      // round up to next integer
      return Math.ceil(count);
    },
    getTotalAmount() {
      return formatNumber(this.totalFXLineTotal, "$ ##,###,###,###,##0.00");
    },
    getSubTotal() {
      return formatNumber(this.TaxableFXLineTotal, "$ ##,###,###,###,##0.00");
    },
    getTaxTotal() {
      return formatNumber(this.TaxFXLineTotal, "$ ##,###,###,###,##0.00");
    },
    getTotalPayment() {
      return formatNumber(this.totalPayment, "$ ##,###,###,###,##0.00");
    },
    getTotalBalance() {
      return formatNumber(this.totalBalance, "$ ##,###,###,###,##0.00");
    },
    getRoundingAmount() {
      return formatNumber(this.totalRounding, "$ ##,###,###,###,##0.00");
    },
    getTotalDue() {
      return formatNumber(
        this.totalFXLineTotal + this.totalRounding,
        "$ ##,###,###,###,##0.00"
      );
    },

    getTotalCashReceived() {
      return formatNumber(this.totalCashReceived, "$ ##,###,###,###,##0.00");
    },
    getTotalChangedDue() {
      return formatNumber(this.totalChangedDue, "$ ##,###,###,###,##0.00");
    },
    isCustomerAccountEnabled() {
      return this.model.CreditLimit == 0 || this.model.TermCalculation == "CBD";
    },
    isProcessSaleEnabled() {
      if (
        this.getTotalItemCount > 0 &&
        this.totalPayment != 0 &&
        this.totalBalance == 0
      ) {
        return true;
      } else return false;
    },
    getPermissionFormItems() {
      return [
        {
          dataField: "userName",
          editorOptions: { showClearButton: true },
          validationRules: [
            {
              type: "required",
              message: "User Name is required",
            },
          ],
        },
        {
          dataField: "password",
          editorOptions: {
            mode: "password",
            showClearButton: true,
          },
          validationRules: [
            {
              type: "required",
              message: "Password is required",
            },
          ],
        },
      ];
    },
  },
  methods: {
    async consumeNextItem() {
      await this.$store.dispatch("posScans/consumeItem");
      if (this.currentBarcodeFromQueue) {
        this.processScan(this.currentBarcodeFromQueue);
      }
    },
    startConsuming() {
      this.intervalId = setInterval(this.consumeNextItem, 1000); // consume every second
    },
    stopConsuming() {
      clearInterval(this.intervalId);
    },
    async onInitialized() {
      await this.$store.dispatch(
        "screenFactors/getFactors",
        [18, 66, 578, 626, 571]
      );
      await this.getPOSDefaultData();

      let response = await DefaultCompanyBranchDivisionService.getDefault();

      if (response.data.value.length > 0) {
        this.model.CompanyCode = response.data.value[0].CompanyCode;
        // this.model.BranchCode = response.data.value[0].BranchCode;
        this.model.DivisionCode = response.data.value[0].DivisionCode;
      }
    },
    async setDeliveryAddressDataSourceItems(customerCode, lineNo) {
      let defaultLineNo = 1;
      if (lineNo) defaultLineNo = +lineNo;

      let deliveryAddresDs = CustomerDeliveryAddressService.dataSourceConfig(
        "api/CustomerDeliveryAddress",
        {
          select: [
            "CompanyName",
            "PhysicalAddressStreetLine1",
            "PhysicalAddressStreetLine2",
            "PhysicalAddressSuburb",
            "PhysicalAddressState",
            "PhysicalAddressPostcode",
            "PhysicalAddressCountry",
            "RecordId",
            "LineNo",
            "TransitDays",
          ],
          key: "RecordId",
          filter: [
            ["Inactive", "=", "N"],
            "and",
            ["CustomerCode", "=", customerCode],
            "and",
            ["LineNo", "=", defaultLineNo],
          ],
        }
      );

      let results = await deliveryAddresDs.load();

      if (results.length > 0) {
        let deliveryAddressLine1Record = results[0];

        if (deliveryAddressLine1Record) {
          this.model.DeliveryAddressCompanyName =
            deliveryAddressLine1Record.CompanyName;

          this.model.DeliveryAddressCountry =
            deliveryAddressLine1Record.PhysicalAddressCountry;
          this.model.DeliveryAddressPostcode =
            deliveryAddressLine1Record.PhysicalAddressPostcode;
          this.model.DeliveryAddressState =
            deliveryAddressLine1Record.PhysicalAddressState;
          this.model.DeliveryAddressStreetLine1 =
            deliveryAddressLine1Record.PhysicalAddressStreetLine1;
          this.model.DeliveryAddressStreetLine2 =
            deliveryAddressLine1Record.PhysicalAddressStreetLine2;
          this.model.DeliveryAddressSuburb =
            deliveryAddressLine1Record.PhysicalAddressSuburb;
          this.model.CustomerDeliveryAddressLineNo =
            deliveryAddressLine1Record.LineNo;
        }
      } else {
        this.model.DeliveryAddressCompanyName = undefined;
        this.model.DeliveryAddressCountry = undefined;
        this.model.DeliveryAddressPostcode = undefined;
        this.model.DeliveryAddressState = undefined;
        this.model.DeliveryAddressStreetLine1 = undefined;
        this.model.DeliveryAddressStreetLine2 = undefined;
        this.model.DeliveryAddressSuburb = undefined;
        this.model.CustomerDeliveryAddressLineNo = undefined;
      }
    },
    headerFormValueChanged(isValid, brokenRules) {
      this.isValid = false;
      if (!isValid) this.displayValidationSummary(brokenRules);
    },
    async getPOSDefaultData() {
      await PointOfSalesService.getPOSDefaultData()
        .then((res) => {
          this.model.CustomerName = res.data.DefaultCustomerName;
          this.model.BranchCode = res.data.DefaultBranchCode;
          this.model.CustomerCode = res.data.DefaultCustomerCode;
          this.model.CurrencyCode = res.data.CustomerCurrency;
          this.model.CompanyCurrency = res.data.CompanyCurrency;
          this.model.DefaultRepCode = res.data.DefaultRepCode;
          this.model.CreditLimit = res.data.CustomerCreditLimit;
          this.model.TermCalculation = res.data.CustomerTermCalculation;

          if (this.model.CustomerCode)
            this.setDeliveryAddressDataSourceItems(this.model.CustomerCode);

          if (res.data.ApplyDiscountPermission == "Y") {
            this.IsDiscountUpdate = true;
            this.DiscountEditPermissionReceived = true;
          } else {
            this.IsDiscountUpdate = false;
            this.DiscountEditPermissionReceived = false;
          }

          if (res.data.PriceEditPermission == "Y") {
            this.IsPriceUpdate = true;
            this.PriceEditPermissionReceived = true;
          } else {
            this.IsPriceUpdate = false;
            this.PriceEditPermissionReceived = false;
          }
        })
        .catch((err) => {
          if (err) this.displayServerError(err, "Default Data Failed");
        });

      this.setExchangeRateProperties();
    },
    async getSalesResForCustomer(customerCode) {
      await PointOfSalesService.getSalesResForCustomer(customerCode)
        .then((res) => {
          this.model.DefaultRepCode = res.data.DefaultRepCode;
        })
        .catch((err) => {
          if (err) this.displayServerError(err, "Default Rep Data Failed");
        });
    },
    onFocusInCustomer(e) {
      if (e.event) {
        e.component.blur();
        this.visibleCustomerSearch = true;
      }
    },
    onHeaderFormContentReady() {
      const headerForm = document.querySelectorAll(".header-form");
      if (headerForm) this.headerFormHeight = headerForm[0].clientHeight;
    },
    async onOrderNoChanged(e) {
      if (getFactorByCode(578).Factorc !== "Y" || e.value == "") return;
      let orderNo = e.value;

      let result = await SalesOrderService.findOrderNoUsageForSalesOrder(
        orderNo,
        false,
        this.model.CustomerCode
      );

      let orderNoUsedPreviously = result.data.value.length > 0;
      if (!orderNoUsedPreviously) {
        result = await SalesOrderService.findOrderNoUsageForInvoice(
          orderNo,
          false,
          this.model.CustomerCode
        );
        orderNoUsedPreviously = result.data.value.length > 0;
      }

      if (orderNoUsedPreviously) {
        // Show prompt
        let confirmOrderNoReuse = await this.$confirm(
          `Customer ${this.model.CustomerName} has used Customer PO ${orderNo} previously. Will you use Customer PO ${orderNo} again?`,
          "Customer PO Used Previously"
        );

        if (!confirmOrderNoReuse) {
          e.component.focus();
          e.component.reset();
          e.element.querySelector("input").select();
        }
      }
    },

    onFieldFocusIn(e, cellInfo) {
      if (this.focusedCellData) {
        this.removeCellFocus();
      }
      e.event.target.select();
      this.onEditFocusIn(e, cellInfo);
      e.element.parentElement.classList.add("key-pad-focused-cell");
      e.component.blur();
    },

    applyFocusClass(inputEl) {
      if (!this.focusedCellData) return;
      if (!inputEl) inputEl = this.focusedCellData.inputEl;
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;
      var cell = dataGrid.getCellElement(
        this.focusedCellData.cellInfo.rowIndex,
        this.focusedCellData.cellInfo.column.dataField
      );
      // var editor = cell.querySelector("div.gc-grid-number-box-editor");
      // if (editor) editor.classList.add("key-pad-focused");
      if (cell) {
        cell.classList.add("key-pad-focused-cell");
        // var dataGrid = this.$refs["dx-data-grid-comp"].instance;
        // dataGrid.refresh(true);
      }
    },
    async onSalesUMQtyChanged(newData, value, currentRowData) {
      if (this.PriceUpdateTimeOutID) clearTimeout(this.PriceUpdateTimeOutID);

      newData.SalesUMQty = value;
      // newData.DiscPcnt = 0;

      // this.PriceUpdateTimeOutID = setTimeout(() => {
      //   this.getItemPriceUpdate({ ...currentRowData, ...newData });
      // }, 250);

      newData.SalesUMNetPrice =
        Math.round(
          currentRowData.SalesUMTaxIncFXPrice *
            (100 / (100 + currentRowData.SalesTaxPcnt)) *
            (100 / (100 + currentRowData.WetPcnt)) *
            100
        ) / 100;
      // newData.SalesUMFXPrice = newData.SalesUMPrice = newData.SalesUMNetPrice;

      newData.SalesUMNetFXPrice = calculateSalesUMNetFXPrice(
        currentRowData.SalesUMFXPrice,
        value
      );

      newData = this.updateLineTotals({ ...currentRowData, ...newData });

      this.localDataSource.update(newData.ID, newData).then(() => {
        var dataGrid = this.$refs["dx-data-grid-comp"].instance;
        dataGrid.refresh(true);
      });

      return newData;
    },
    updateLineTotals(detail) {
      detail.SalesUMTaxIncNetFXPrice = this.roundToTwoDecimals(
        (detail.SalesUMTaxIncFXPrice * (100 - detail.DiscPcnt)) / 100
      );
      if (detail.WetPcnt === 0 && detail.SalesTaxPcnt === 0) {
        detail.DiscLineTotal = this.roundToTwoDecimals(
          detail.SalesUMPrice * detail.SalesUMQty -
            detail.SalesUMNetPrice * detail.SalesUMQty
        );
        detail.FXLineTotal = this.roundToTwoDecimals(
          detail.SalesUMQty * detail.SalesUMNetPrice
        );
        detail.NetLineTotal = this.roundToTwoDecimals(
          detail.SalesUMQty * detail.SalesUMNetPrice
        );
        detail.LineTotal = this.roundToTwoDecimals(
          detail.SalesUMNetPrice * detail.SalesUMQty
        );
      } else {
        detail.DiscLineTotal = this.roundToTwoDecimals(
          detail.SalesUMPrice * detail.SalesUMQty -
            detail.SalesUMNetPrice * detail.SalesUMQty
        );
        detail.FXLineTotal = this.roundToTwoDecimals(
          detail.SalesUMQty * detail.SalesUMTaxIncNetFXPrice
        );
        detail.NetLineTotal = this.roundToTwoDecimals(
          detail.SalesUMQty * detail.SalesUMNetPrice
        );
        detail.LineTotal = this.roundToTwoDecimals(
          detail.SalesUMTaxIncNetFXPrice * detail.SalesUMQty
        );
      }

      if (detail.WetPcnt === 0) {
        detail.TaxLineTotal = this.roundToTwoDecimals(
          detail.LineTotal - detail.NetLineTotal
        );
        detail.TaxableLineTotal = detail.NetLineTotal;
        detail.WetLineTotal = this.roundToTwoDecimals(0);
      } else {
        detail.TaxLineTotal = this.roundToTwoDecimals(
          (detail.LineTotal * detail.SalesTaxPcnt) / (detail.SalesTaxPcnt + 100)
        );
        detail.TaxableLineTotal = this.roundToTwoDecimals(
          detail.LineTotal - detail.TaxLineTotal
        );
        detail.WetLineTotal = this.roundToTwoDecimals(
          detail.TaxableLineTotal - detail.NetLineTotal
        );
      }

      detail.DiscFXLineTotal = detail.DiscLineTotal;
      detail.NetFXLineTotal = detail.NetLineTotal;
      detail.TaxFXLineTotal = detail.TaxLineTotal;
      detail.TaxableFXLineTotal = detail.TaxableLineTotal;
      detail.WetFXLineTotal = detail.WetLineTotal;

      return detail;
    },
    roundToTwoDecimals(value) {
      return Math.round(value * 100) / 100;
    },
    async onDiscPcntChanged(newData, value, currentRowData) {
      newData.DiscPcnt = value;
      if (currentRowData.SalesUMQty > 0) {
        newData.SalesUMNetPrice = (
          currentRowData.SalesUMPrice -
          (currentRowData.SalesUMPrice * newData.DiscPcnt) / 100
        ).roundNumber(4);

        // newData.SalesUMTaxIncNetFXPrice = (
        //   newData.SalesUMNetPrice *
        //   ((100 + currentRowData.WetPcnt) / 100) *
        //   ((100 + currentRowData.SalesTaxPcnt) / 100)
        // ).roundNumber(2);

        newData = this.updateLineTotals({ ...currentRowData, ...newData });
      }

      this.localDataSource.update(newData.ID, newData).then(() => {
        var dataGrid = this.$refs["dx-data-grid-comp"].instance;
        dataGrid.refresh(true);
      });

      return newData;
    },
    async onSalesUMFXPriceChanged(newData, value, currentRowData) {
      newData.SalesUMTaxIncFXPrice = value;

      newData.SalesUMNetPrice =
        Math.round(
          newData.SalesUMTaxIncFXPrice *
            (100 / (100 + currentRowData.SalesTaxPcnt)) *
            (100 / (100 + currentRowData.WetPcnt)) *
            100
        ) / 100;
      newData.SalesUMFXPrice =
        newData.SalesUMNetFXPrice =
        newData.SalesUMPrice =
          newData.SalesUMNetPrice;

      newData = this.updateLineTotals({ ...currentRowData, ...newData });

      this.localDataSource.update(newData.ID, newData).then(() => {
        var dataGrid = this.$refs["dx-data-grid-comp"].instance;
        dataGrid.refresh(true);
      });
    },
    onSalesUMChanged(newData, value, currentRowData) {
      newData.UnitOfMeasure = value;
      newData.SalesUM = value;
      newData.SalesUMQty = currentRowData.SalesUMQty;
      this.getItemPriceUpdate({ ...currentRowData, ...newData });
    },
    onSalesUMFocusIn(e, cellInfo) {
      if (!e.component.option().readOnly)
        this.setPopupVisible(e, "SalesUM", {
          ItemCode: cellInfo.data.ItemCode,
          cellInfo: cellInfo,
        });
      else e.component.blur();
    },
    setPopupVisible(e, type, params) {
      e.component.blur();
      switch (type) {
        case "SalesUM":
          this.selectedRowData = params.cellInfo;
          var SaleUMs = params.cellInfo.data.SalesUMs;
          SaleUMs.forEach((element) => {
            element.ItemCode = params.ItemCode;
          });
          this.popupConfig = {
            title: "SalesUM Search",
            dataGrid: {
              toolbar: {
                salesrep: false,
              },
              options: {
                multipleSelect: false,
                onRowClick: this.onSalesUMRowClick,
                keyExpr: "SalesUM",
              },
              dataSource: SaleUMs,
              columns: [
                {
                  caption: "SalesUM",
                  dataField: "UnitOfMeasureCode",
                },
                {
                  caption: "ItemCode",
                  dataField: "ItemCode",
                  visible: false,
                },
              ],
            },
          };

          this.popupVisible = true;

          break;
        default:
          break;
      }
    },
    onSalesUMRowClick(e) {
      this.popupHidden();
      var rowIndex = this.selectedRowData.rowIndex;
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;
      dataGrid.cellValue(rowIndex, "UnitOfMeasure", e.data.UnitOfMeasureCode);

      this.selectedRowData = undefined;
    },
    onCopyLine(e) {
      this.showLoading(true);

      let maxID = Math.max(...this.localDataSource._array.map((e) => e.ID));
      let data = { ...e.row.data };
      data.ID = ++maxID;

      this.localDataSource._array.splice(e.row.rowIndex + 1, 0, data);

      setTimeout(() => {
        this.$nextTick(() => {
          var dataGrid = this.$refs["dx-data-grid-comp"].instance;
          dataGrid.refresh(true);

          var ds = dataGrid.getDataSource();
          ds.reload();

          this.showLoading(false);
        });
      }, 1);
    },
    setCashPayment() {
      this.visiblePayment = false;
      this.$nextTick(() => {
        this.PaymentMethod = "CASH";
        this.visibleCashPayment = true;
      });
    },
    async onConfirmPayment() {
      this.popupHidden();
      if (
        this.totalCashReceived - (this.totalFXLineTotal + this.totalRounding) >
        0
      ) {
        await this.$alert(
          `Change amount ${this.getTotalChangedDue}. Please give the change to the customer`,
          "Warning"
        );
      }
      this.totalPayment = (
        this.totalCashReceived - this.totalChangedDue
      ).roundNumber(2);
      this.totalBalance = (
        this.totalFXLineTotal +
        this.totalRounding -
        this.totalPayment
      ).roundNumber(2);
      this.totalCashReceived = 0;
      this.$nextTick(() => {
        this.$notify(
          "Payment successfully paid. Don’t forget to send off the customer!",
          "success"
        );
      });
    },
    cancelCashModel() {
      this.totalCashReceived = 0;
      this.totalRounding = 0;
      this.totalPayment = 0;
      this.popupHidden();
    },
    onShowChequeOption() {
      this.$nextTick(function () {
        let myDialog = custom({
          title: "Cheque",
          messageHtml: `Please confirm you received ${this.getTotalAmount} cheque ?`,
          buttons: [
            {
              text: "No, I Didn't",
              elementAttr: {
                class: "no-cheque",
              },
              onClick: (e) => {
                return { buttonText: e.component.option("text") };
              },
            },
            {
              text: "Yes, Confirm",
              elementAttr: {
                class: "yes-cheque",
              },
              onClick: (e) => {
                return { buttonText: e.component.option("text") };
              },
            },
          ],
        });
        myDialog.show().then((dialogResult) => {
          if (dialogResult.buttonText == "Yes, Confirm") {
            this.totalCashReceived = this.totalFXLineTotal;
            this.PaymentMethod = "CHEQUE";
            this.onConfirmPayment();
          }
        });
      });
    },
    onShowCardOption() {
      this.$nextTick(function () {
        let myDialog = custom({
          title: "Card",
          messageHtml: `Please confirm you received ${this.getTotalAmount} by card ?`,
          buttons: [
            {
              text: "No, I Didn't",
              elementAttr: {
                class: "no-cheque",
              },
              onClick: (e) => {
                return { buttonText: e.component.option("text") };
              },
            },
            {
              text: "Yes, Confirm",
              elementAttr: {
                class: "yes-cheque",
              },
              onClick: (e) => {
                return { buttonText: e.component.option("text") };
              },
            },
          ],
        });
        myDialog.show().then((dialogResult) => {
          if (dialogResult.buttonText == "Yes, Confirm") {
            this.totalCashReceived = this.totalFXLineTotal;
            this.PaymentMethod = "EFTPOS";
            this.onConfirmPayment();
          }
        });
      });
    },
    onAccountPayment() {
      this.totalCashReceived = this.totalFXLineTotal;
      this.PaymentMethod = undefined;
      this.onConfirmPayment();
    },
    onProcessSale() {
      let payload = {
        IncludesGeneralLedgerLines: "N",
        PaymentMethod: this.PaymentMethod,
        OrderSource: "POS",
        BranchCode: this.model.BranchCode,
        Code: 0,
        CompanyCode: getFactorByCode(18).Factorc,
        CurrencyCode: this.model.CurrencyCode,
        CustomerCode: this.model.CustomerCode,
        CustomerDeliveryAddressLineNo:
          this.model.CustomerDeliveryAddressLineNo.toString(),
        DateIn: new Date().toSaveDate(),
        DeliveryAddressCompanyName: this.model.DeliveryAddressCompanyName,
        DeliveryAddressCountry: this.model.DeliveryAddressCountry,
        DeliveryAddressPostcode: this.model.DeliveryAddressPostcode,
        DeliveryAddressState: this.model.DeliveryAddressState,
        DeliveryAddressStreetLine1: this.model.DeliveryAddressStreetLine1,
        DeliveryAddressStreetLine2: this.model.DeliveryAddressStreetLine2,
        DeliveryAddressSuburb: this.model.DeliveryAddressSuburb,
        Status: "N",
        DivisionCode: getFactorByCode(66).Factorc,
        ExchangeRate: this.model.ExchangeRate,
        ExTaxTotal: parseFloat(this.TaxableFXLineTotal.toFixed(2)),
        ItemCount: this.getTotalItemCount,
        RequiredByDate: new Date().toSaveDate(),
        SalesOrderTypeCode: getFactorByCode(626).Factorc,
        SalesRepresentativeCode: this.model.DefaultRepCode,
        ShipDate: new Date().toSaveDate(),
        TaxTotal: parseFloat(this.TaxFXLineTotal.toFixed(2)),
        Total: parseFloat(
          (this.TaxableFXLineTotal + this.TaxFXLineTotal).toFixed(2)
        ),
        CommissionPcnt: 0,
        Rounding: parseFloat(this.totalRounding.toFixed(2)),
        OrderNo: this.model.OrderNo,
        SalesOrderDetails: this.localDataSource._array.map((x) => {
          return {
            SalesOrderCode: 0,
            LineNo: 0,
            ItemCode: x.ItemCode,
            SalesUM: x.UnitOfMeasure,
            SalesUMQty: x.SalesUMQty,
            DiscPcnt: x.DiscPcnt,
            SalesUMFXPrice: x.SalesUMFXPrice,
            Comment: "",
            IsCommentVisible: false,
            RecordId: "00000000-0000-0000-0000-000000000000",
            CustomerSalesContractCode: null,
            CustomerSalesContractLineNo: null,
            ProductBatches: [],
            ItemType: "S",
            TaxCode: x.TaxCode,
            SalesUMTaxIncNetFXPrice: x.SalesUMTaxIncNetFXPrice,
          };
        }),
      };

      this.showLoading(true);
      PointOfSalesService.savePOS(payload)
        .then((res) => {
          if (res) {
            this.showLoading(false);
            // if (this.PaymentMethod) {
            //   this.ReceiptCode = res.data.CustomerReceiptCode;
            // } else
            this.ReceiptCode = res.data.Code;
          }
          this.IsPrintReceipt = true;
          this.$notify(
            `Point of Sale Transaction ${res.data.Code} has been successfully processed.`,
            "success"
          );

          this.loadDefaultScreen();
        })
        .catch((err) => {
          this.showLoading(false);
          this.displayServerError(err, "POS Failed");
        });
    },
    loadDefaultScreen() {
      this.localDataSource.clear();
      this.totalCashReceived = 0;
      this.totalFXLineTotal = 0;
      this.totalRounding = 0;
      this.totalPayment = 0;
      this.totalBalance = 0;
      this.totalChangedDue = 0;
      this.PaymentMethod = undefined;
      this.TaxableFXLineTotal = 0;
      this.TaxFXLineTotal = 0;
      this.model.OrderNo = undefined;
      this.getPOSDefaultData();
    },
    async getUserPermission() {
      const userPermissionForm = await validationEngine.validateGroup(
        "permission-form"
      );

      if (!userPermissionForm.isValid) {
        this.displayValidationSummary(userPermissionForm.brokenRules);
      } else {
        this.showLoading(true);
        await PointOfSalesService.getPriceDiscountPermission({
          Username: this.permissionFormData.userName,
          Password: this.permissionFormData.password,
          PermissionDescription: this.PermissionDescription,
        })
          .then((res) => {
            this.showLoading(false);

            if (res) {
              if (this.PermissionDescription == "POS Edit Discount") {
                if (res.data.HasPermission == "N") {
                  this.displayValidationSummary([
                    {
                      message: `You do not have permission to access the POS Discount feature. Contact your system administrator for assistance.`,
                    },
                  ]);
                  this.IsDiscountUpdate = false;
                } else {
                  this.IsDiscountUpdate = true;
                  this.DiscountEditPermissionReceived = true;
                  this.visiblePermission = false;
                }
              }

              if (this.PermissionDescription == "POS Edit Sell Price") {
                if (res.data.HasPermission == "N") {
                  this.displayValidationSummary([
                    {
                      message: `You do not have permission to access the POS Price Edit feature. Contact your system administrator for assistance.`,
                    },
                  ]);
                  this.IsPriceUpdate = false;
                } else {
                  this.IsPriceUpdate = true;
                  this.PriceEditPermissionReceived = true;
                  this.visiblePermission = false;
                }
              }
            }
          })
          .catch((err) => {
            this.showLoading(false);
            this.displayServerError({
              message: err.data,
            });
          });
      }
    },
    onCancelPermission() {
      if (this.PermissionDescription == "POS Edit Discount")
        this.IsDiscountUpdate = false;
      if (this.PermissionDescription == "POS Edit Sell Price")
        this.IsPriceUpdate = false;

      this.visiblePermission = false;
    },

    // Customer Select Popup
    onCustomerSelectionChanged(e) {
      if (e.selectedRowsData.length > 0) {
        if (e.selectedRowsData[0].StopCredit != "Y") {
          this.model.CustomerCode = e.selectedRowsData[0].Code;
          this.model.CustomerName = e.selectedRowsData[0].Name;
          this.model.CurrencyCode = e.selectedRowsData[0].CurrencyCode;
          this.model.CreditLimit = e.selectedRowsData[0].CreditLimit;
          this.model.TermCalculation = e.selectedRowsData[0].TermCalculation;

          this.getSalesResForCustomer(this.model.CustomerCode);
          this.setDeliveryAddressDataSourceItems(this.model.CustomerCode);
          this.popupHidden();
          this.setExchangeRateProperties();
        }
      }
    },
    onRowPrepared(e) {
      if (e.rowType == "data") {
        if (e.data.StopCredit == "Y") {
          e.rowElement.classList.add("gc-disable-row");
        }
      }
    },
    setVisibleProductSearch() {
      this.selectedProducts = [];
      this.visibleProductSearch = true;
    },
    onProductSelectionChanged(e) {
      let submitBtn = this.$refs["submit-btn"].instance;

      if (e.currentSelectedRowKeys.length > 0) {
        let index = this.selectedProducts.findIndex(
          (x) => x.ItemCode == e.currentSelectedRowKeys[0].ItemCode
        );
        if (index == -1) {
          let item = e.selectedRowsData.find(
            (x) => x.ItemCode == e.currentSelectedRowKeys[0].ItemCode
          );
          if (item) {
            item.ItemType = "S";
            item.SalesUM = item.UnitOfMeasure;
            item.SalesUMQty = 1;
            // this.selectedProducts.push(item);
            this.getItemPriceUpdate(item);
          }
        }
      }

      if (e.currentDeselectedRowKeys.length > 0) {
        let index = this.selectedProducts.findIndex(
          (x) => x.ItemCode == e.currentDeselectedRowKeys[0].ItemCode
        );
        if (index != -1) this.selectedProducts.splice(index, 1);
      }

      if (e.selectedRowsData.length > 0) {
        // this.selectedProducts = e.selectedRowsData;
        submitBtn.option("disabled", false);
      } else {
        this.selectedProducts = [];
        submitBtn.option("disabled", true);
      }
    },
    onSelectedProduct() {
      this.popupHidden();
      let maxID = Math.max(...this.localDataSource._array.map((e) => e.ID));
      if (!maxID || maxID == -Infinity) maxID = 0;

      this.selectedProducts.forEach((element) => {
        element.ID = ++maxID;
        element.DiscPcnt = 0;
        this.localDataSource.insert(element);
      });

      let paymentBtn = this.$refs["payment-btn"].instance;
      if (this.selectedProducts.length > 0) {
        this.IsPrintReceipt = false;
        this.ReceiptCode = undefined;
        this.receiptURL = undefined;
        paymentBtn.option("disabled", false);
      } else {
        paymentBtn.option("disabled", true);
      }
    },
    popupHidden() {
      let customerGrid = this.$refs["customer-selection-grid"];
      if (customerGrid) {
        customerGrid.instance.clearSorting();
      }
      this.visibleCustomerSearch = false;
      this.visibleProductSearch = false;
      this.popupVisible = false;
      this.visiblePayment = false;
      this.visibleCashPayment = false;
      this.visibleReceipt = false;
      this.visiblePermission = false;
      this.permissionFormData = {
        userName: undefined,
        password: undefined,
      };
    },

    async setExchangeRateProperties() {
      if (this.model.CompanyCurrency != this.model.CurrencyCode) {
        let result = await CompanyCurrencyService.getByCurrencyCode(
          this.model.CurrencyCode,
          {
            select: ["BuyRate", "CurrencyCode", "SellRate"],
          }
        );
        this.model.ExchangeRate = result.data.value[0].BuyRate;
      } else {
        this.model.ExchangeRate = 1;
      }
    },

    async getItemPriceUpdate(data, type) {
      SalesOrderV3Service.setSaleItemPrice(
        data,
        data,
        getFactorByCode(626).Factorc,
        this.model.BranchCode,
        this.model.CustomerCode,
        this.model.ExchangeRate,
        this.model.DateIn,
        this.model.RequiredByDate,
        false
      ).then((res) => {
        let salesUMTaxIncFXPrice = (
          res.SalesUMNetPrice *
          ((100 + res.WetPcnt) / 100) *
          ((100 + res.SalesTaxPcnt) / 100)
        ).roundNumber(2);

        res.SalesUMTaxIncFXPrice = salesUMTaxIncFXPrice;

        res.SalesUMNetPrice =
          Math.round(
            salesUMTaxIncFXPrice *
              (100 / (100 + res.SalesTaxPcnt)) *
              (100 / (100 + res.WetPcnt)) *
              100
          ) / 100;

        res.SalesUMFXPrice =
          res.SalesUMNetFXPrice =
          res.SalesUMPrice =
            res.SalesUMNetPrice;

        var updatedData = this.updateLineTotals({ ...res });

        if (type == "insert") {
          this.addNewPOSProduct(updatedData);
        } else {
          if (data.ID) {
            this.localDataSource.update(data.ID, updatedData).then(() => {
              var dataGrid = this.$refs["dx-data-grid-comp"].instance;
              dataGrid.refresh(true);
            });
          } else {
            updatedData.SalesUMTaxIncFXPrice = salesUMTaxIncFXPrice;
            updatedData.SalesUMTaxIncNetFXPrice = salesUMTaxIncFXPrice;
            updatedData.OriginSalesUMTaxIncFXPrice = salesUMTaxIncFXPrice;
            updatedData.FXLineTotal = salesUMTaxIncFXPrice;
            this.selectedProducts.push({ ...data, ...updatedData });
          }
        }
      });
    },
    addNewPOSProduct(item) {
      let maxID = Math.max(...this.localDataSource._array.map((e) => e.ID));
      if (!maxID || maxID == -Infinity) maxID = 0;

      item.ID = ++maxID;
      item.DiscPcnt = 0;

      item.SalesUMTaxIncFXPrice = item.FXLineTotal;
      item.SalesUMTaxIncNetFXPrice = item.FXLineTotal;
      item.OriginSalesUMTaxIncFXPrice = item.FXLineTotal;
      this.localDataSource.insert(item);

      let paymentBtn = this.$refs["payment-btn"].instance;
      if (this.selectedProducts.length > 0) {
        this.IsPrintReceipt = false;
        this.ReceiptCode = undefined;
        this.receiptURL = undefined;
        paymentBtn.option("disabled", false);
      } else {
        paymentBtn.option("disabled", true);
      }
    },

    removeCellFocus() {
      var cell = document.querySelectorAll(".key-pad-focused-cell");
      if (cell.length > 0) cell[0].classList.remove("key-pad-focused-cell");
    },

    onEditorPreparing(e) {
      if (e.editorOptions && e.editorOptions.onFocusIn) {
        let func = e.editorOptions.onFocusIn;
        if (this.baseView.mode != "review" && e.parentType != "searchPanel")
          e.editorOptions.onFocusIn = (event) => {
            func(event, {
              column: {
                dataField: e.dataField,
              },
              data: e.row.data,
              rowIndex: e.row.rowIndex,
            });
          };
      }
    },
    onCellPrepared(e) {
      if (e.rowType == "data") {
        if (e.column.dataField == "UnitOfMeasure") {
          if (e.data.SalesUMs.length > 1) {
            e.cellElement.classList.add("gc-editable-cell-color");
          }
        }
      }
    },
    onRowRemoved(e) {
      if (e.data) {
        this.$notify("Entry deleted Successfully.", "Success");
      }
    },
    async onEditFocusIn(e, cellInfo) {
      let inputEl = e.element.querySelector(".dx-texteditor-input");

      this.focusedCellData = {
        e: e,
        cellInfo: cellInfo,
        requestSent: false,
        inputEl: inputEl,
      };

      // e.component.blur();
      // this.showNumericKeyPad();

      setTimeout(() => {
        this.applyFocusClass(inputEl);
      }, 1);

      this.$store.dispatch("numericInputStore/setFieldFocused", false);
      this.$store.dispatch("numericInputStore/keyPadFocusChanged");
      this.$store.dispatch("numericInputStore/updateRecord", {
        itemCode: cellInfo.data.ItemCode,
        value: e.component._parsedValue,
      });
    },
    setPriceUpdate(e) {
      if (e.event) {
        if (this.PriceEditPermissionReceived) {
          if (!e.value) {
            this.removeCellFocus();
            this.focusedCellData = undefined;
            this.localDataSource._array.forEach((element) => {
              element.SalesUMTaxIncFXPrice = element.OriginSalesUMTaxIncFXPrice;

              element.SalesUMNetPrice =
                Math.round(
                  element.SalesUMTaxIncFXPrice *
                    (100 / (100 + element.SalesTaxPcnt)) *
                    (100 / (100 + element.WetPcnt)) *
                    100
                ) / 100;

              element.SalesUMFXPrice =
                element.SalesUMNetFXPrice =
                element.SalesUMPrice =
                  element.SalesUMNetPrice;

              element = this.updateLineTotals({ ...element, ...element });

              this.localDataSource.update(element.ID, element);
            });
          }
          setTimeout(() => {
            var dataGrid = this.$refs["dx-data-grid-comp"].instance;
            dataGrid.refresh(true);
          }, 1);
        } else {
          if (e.value) {
            this.PermissionTitle = "Permission required for Price Edit";
            this.PermissionDescription = "POS Edit Sell Price";
            this.visiblePermission = true;
          }
        }
      }
    },
    setDiscountUpdate(e) {
      if (e.event) {
        if (this.DiscountEditPermissionReceived) {
          if (!e.value) {
            this.removeCellFocus();
            this.focusedCellData = undefined;
            this.localDataSource._array.forEach((element) => {
              if (element.DiscPcnt != 0) {
                element.DiscPcnt = 0;
                if (element.SalesUMQty > 0) {
                  element.SalesUMNetPrice = element.SalesUMNetPrice = (
                    element.SalesUMPrice -
                    (element.SalesUMPrice * element.DiscPcnt) / 100
                  ).roundNumber(4);

                  element.SalesUMTaxIncFXPrice = (
                    element.SalesUMNetPrice *
                    ((100 + element.WetPcnt) / 100) *
                    ((100 + element.SalesTaxPcnt) / 100)
                  ).roundNumber(2);

                  element = this.updateLineTotals({
                    ...element,
                    ...element,
                  });
                }

                this.localDataSource.update(element.ID, element);
              }
            });
          }

          var dataGrid = this.$refs["dx-data-grid-comp"].instance;
          dataGrid.refresh(true);
        } else {
          if (e.value) {
            this.PermissionTitle = "Permission required for discount";
            this.PermissionDescription = "POS Edit Discount";
            this.visiblePermission = true;
          }
        }
      }
    },
    onCashPaymentShown() {
      this.totalChangedDue = 0;
      this.setRoundingAmount(this.totalFXLineTotal);
    },
    setRoundingAmount(total) {
      let value = Math.abs((total * 100) % 5);
      switch (Math.round(value).toString()) {
        case "1":
          this.totalRounding = -0.01;
          break;
        case "2":
          this.totalRounding = -0.02;
          break;
        case "3":
          this.totalRounding = 0.02;
          break;
        case "4":
          this.totalRounding = 0.01;
          break;
        case "6":
          this.totalRounding = -0.01;
          break;
        case "7":
          this.totalRounding = -0.02;
          break;
        case "8":
          this.totalRounding = 0.02;
          break;
        case "9":
          this.totalRounding = 0.01;
          break;
        default:
          this.totalRounding = 0;
          break;
      }
    },
    async onPrintReceiptShown() {
      if (!this.ReceiptCode) return;

      var response;
      await TransactionReportService.getByFeatureCode("ReportPOSInvoice")
        .then((res) => {
          response = res;
        })
        .catch((err) => {
          if (err) this.displayServerError(err, "Report repx not found");
        });

      var report = undefined;
      if (response && response.data) {
        report = response.data;
        report.Parameters = JSON.parse(response.data.Parameters);

        let link = `/api/GetReportPdf?reportLocation=${report.Location}`;

        report.Parameters.forEach((element) => {
          let globalField = { ...getGlobalField(element.screenField) };

          if (globalField) {
            link += `&${globalField.dataField}=${this.ReceiptCode}`;
          }
        });

        this.showLoading(true);
        await ApiService.get(link)
          .then((res) => {
            const blob = b64toBlob(res.data, "application/pdf");
            const blobUrl = URL.createObjectURL(blob);
            this.showLoading(false);

            this.receiptURL = Object.assign(document.createElement("a"), {
              href: blobUrl,
            });
          })
          .catch((error) => {
            this.$logger.debug("ExportToPdf Error ", error);
            this.showLoading(false);
          });
      }
    },
    // BarCode Scanning
    async barcodeScanned(Barcode) {
      this.$store.dispatch("posScans/addItem", Barcode.trim());
    },
    async processScan(Barcode) {
      await PointOfSalesService.validatePOSBarcode({
        filter: `Barcode eq '${Barcode}' and BranchCode eq '${this.model.BranchCode}'`,
      })
        .then(async (res) => {
          let data = res.data.value;
          if (data.length == 0) {
            await this.$alert(
              `There are no products that match this barcode. Please manually add this product using the search.`,
              "Warning"
            );
          } else if (data.length > 1) {
            await this.$alert(
              `There are multiple products that match this barcode. Please manually add this product using the search.`,
              "Warning"
            );
          } else {
            let item = data[0];
            item.ItemType = "S";
            item.SalesUM = item.UnitOfMeasure;
            item.SalesUMQty = 1;

            let index = this.selectedProducts.findIndex(
              (x) => x.ItemCode == item.ItemCode
            );
            if (index == -1) this.selectedProducts.push(item);
            this.getItemPriceUpdate(item, "insert");
          }
        })
        .catch((err) => {
          if (err) this.displayServerError(err, "Barcode Scanning Failed");
        });
    },
  },
  watch: {
    numericValue: {
      handler(val) {
        if (this.visibleCashPayment) {
          let numBox = this.$refs["cash-num-box"].instance;
          if (numBox) {
            // numBox.option("value", val);
            this.totalCashReceived = val;
            if (
              this.totalCashReceived > 0 &&
              this.totalCashReceived >
                this.totalFXLineTotal + this.totalRounding
            ) {
              this.totalChangedDue =
                this.totalCashReceived -
                (this.totalFXLineTotal + this.totalRounding);
            } else {
              this.totalChangedDue = 0;
            }
          }
        } else {
          if (val === undefined) return;
          if (this.focusedCellData && this.fieldFocused) {
            var dataGrid = this.$refs["dx-data-grid-comp"].instance;
            dataGrid.cellValue(
              this.focusedCellData.cellInfo.rowIndex,
              this.focusedCellData.cellInfo.column.dataField,
              val
            );
            setTimeout(() => {
              this.applyFocusClass();
            }, 50);
          }
        }
      },
      deep: true,
    },
    localDataSource: {
      handler(val) {
        if (this.focusedCellData && this.fieldFocused) {
          setTimeout(() => {
            this.applyFocusClass();
          }, 50);
        }

        let total = 0;
        this.TaxableFXLineTotal = 0;
        this.TaxFXLineTotal = 0;
        val._array.forEach((element) => {
          total += element.FXLineTotal;
          this.TaxableFXLineTotal += element.TaxableFXLineTotal;
          this.TaxFXLineTotal += element.TaxFXLineTotal;
        });

        this.totalFXLineTotal = total.roundNumber(2);
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss">
.point-of-sale {
  .header-form {
    background-color: white;
    // margin: 12px;
    padding: 12px;
    padding-right: 0px !important;
    margin-bottom: 12px !important;

    .prod-search-btn {
      .dx-item-content.dx-box-item-content {
        text-align: center;
      }
    }
  }

  .details-grid {
    // margin: 0 12px 0 12px;
    .dx-datagrid .dx-row.dx-data-row.dx-row-lines .key-pad-focused-cell {
      background-color: #d1f7ffba !important;
    }

    .dx-datagrid .dx-datagrid-content .dx-datagrid-table .dx-row {
      .dx-editor-cell,
      .dx-editor-cell.key-pad-focused-cell {
        padding-left: 5px !important;
        padding-right: 5px !important;
      }
    }
    .gc-editable-cell-color[role="gridcell"]:not(.cell-disabled) {
      background-color: #f8fdfe !important;
      // #d1f7ffba !important
    }
  }

  .footer {
    display: inline-flex;
    width: 100%;
    justify-content: space-between;
    background-color: white;
    margin-top: 12px;
    padding-top: 0px !important;
    padding-left: 12px;
    padding-right: 12px;
    align-items: center;
    > div {
      display: inline-flex;
    }

    .permission {
      > div:first-child {
        padding-right: 25px;
      }
    }

    .totals {
      font-weight: bold;
      > div:first-child {
        padding-right: 25px;
      }
    }
  }

  .pos-entry {
    display: inline-flex;
    width: 100%;
    height: 100%;

    div.transaction-screen {
      width: calc(100% - 250px);
    }

    div.num-pad {
      width: 250px;
      background-color: white;
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      .number-entry-cell {
        div.dx-button {
          background-color: #ebf1f5;
        }

        div.dx-button.dx-state-hover {
          background-color: #ebf1f575;
        }
      }

      .number-entry-modal-body {
        padding: 12px;

        .number-entry-row > div:first-child {
          margin-left: 0 !important;
        }

        .number-entry-cell.last {
          margin-right: 5px !important;
        }
      }

      .totals {
        padding: 12px;
        margin: 0 12px;
        background-color: #ebf1f5;

        .sub {
          display: flex;
          justify-content: space-between;
        }

        .sub.topline {
          font-size: 13px;
          color: #464646;
          line-height: 20px;
          font-weight: 400;

          > div:last-child {
            font-size: 14px;
            font-weight: 600;
          }
        }

        .sub.last {
          font-size: 16px;
          color: #0e2939;
          line-height: 30px;
          font-weight: 700;

          > div:last-child {
            font-size: 18px;
            font-weight: 700;
            line-height: 30px;
          }
        }
      }

      .action-btns {
        padding: 12px;
        display: flex;
        flex-direction: column;

        .dx-button:not(:last-child) {
          margin-bottom: 15px;
        }
      }
    }
  }
}
.customer-search-popup {
  .dx-popup-content {
    padding: 12px !important;
  }

  .footer {
    margin-top: 12px;
  }

  .gc-disable-row.dx-state-hover:not(.dx-row-alt) td {
    background-color: unset !important;
  }

  .gc-disable-row {
    color: #0000003d !important;
    .record-secondary.gc-grid-code {
      color: #0000003d !important;
    }
  }

  .gc-disable-row > td,
  .gc-disable-row.dx-state-hover > td {
    color: #0000003d !important;
  }

  .gc-disable-row.dx-selection.dx-row-alt > td {
    background-color: #f5f5f5 !important;
  }
}

.search-products-popup {
  .footer {
    .dx-button:first-child {
      margin-right: 12px;
    }
  }
}

.payment-popup {
  .dx-popup-content {
    padding: 12px !important;

    .total-counts {
      background-color: white;
      padding: 12px;
      display: inline-flex;
      width: 100%;
      justify-content: space-between;
    }

    .item-count {
      span:first-child {
        padding-right: 15px;
        font-size: 14px;
        line-height: 20px;
        font-weight: 400;
      }

      span:last-child {
        font-size: 14px;
        line-height: 20px;
        font-weight: 600;
      }
    }

    .totals {
      display: inline-flex;

      div span:first-child {
        padding-right: 15px;
        font-size: 14px;
        line-height: 20px;
        font-weight: 400;
      }

      div span:last-child {
        padding-right: 15px;
        font-size: 14px;
        line-height: 20px;
        font-weight: 600;
      }

      > div:first-child {
        padding-right: 20px;
      }

      div > span:first-child {
        padding-right: 15px;
      }
    }

    .btns-container {
      margin-top: 12px;
      background-color: white;
      padding: 30px;
      height: 520px;
      margin-bottom: 12px;

      > div {
        display: inline-flex;
        width: 100%;
        justify-content: space-around;
      }

      > div:first-child {
        margin-bottom: 40px;
      }

      .dx-button {
        background-color: #daf0ff;

        .dx-button-text {
          font-weight: 700;
          font-size: 18px;
          line-height: 28px;
        }
      }
    }
    .footer {
      padding-right: 0 !important;
    }
  }
}

.cash-popup {
  .dx-popup-content {
    padding: 12px !important;

    .cash-content {
      background-color: white;
      padding: 12px;
      display: inline-flex;
      width: 100%;
      justify-content: space-evenly;

      > div:first-child {
        width: 300px;
        padding: 20px;
        background-color: #ebf1f5;
        border-radius: 10px;

        .dx-numberbox {
          margin: 0 12px;
        }
      }

      > div:last-child {
        width: 250px;
        padding: 20px;
        background-color: #ebf1f5;
        border-radius: 10px;

        display: flex;
        flex-direction: column;
        justify-content: space-between;
        > div {
          display: flex;
          flex-direction: column;

          hr {
            background-color: #e7e7e7;
            width: 100%;
          }

          > div.top-line {
            .label,
            .value {
              color: #0e2939;
              font-weight: 600;
              font-size: 14px;
              line-height: 20px;
            }
          }

          > div:not(:first-child) {
            color: #464646;
            font-weight: 400;
            font-size: 13px;
            line-height: 20px;
          }
          > div:not(.dx-button) {
            display: inline-flex;
            justify-content: space-between;
          }
        }
      }
    }

    .footer {
      margin-top: 12px;
      padding-right: 0 !important;
    }
  }
}

.no-cheque {
  background-color: #e8384f !important;
  color: white !important;
}

.yes-cheque {
  background-color: #03a9f4 !important;
  color: white !important;
}

.receipt-popup {
  .dx-popup-content {
    padding: 12px !important;
    .receipt-content {
      background-color: white;
      padding: 12px;
      height: 94%;
      margin-bottom: 12px;
    }

    .footer {
      padding-right: 0 !important;
    }
  }
}

.permission-popup {
  .dx-popup-content {
    padding: 12px !important;
    .permission-content {
      background-color: white;
      padding: 12px;
      margin-bottom: 12px;
    }

    .footer {
      padding-right: 0px !important;

      > .dx-button:first-child {
        margin-right: 12px;
      }
    }
  }
}
</style>
